import React, { FC } from 'react';
import { SegmentControl, DropdownComponent } from 'components';
import { useSelector, useDispatch } from 'react-redux';
import {
    DEFAULT_PLOT_RANGE,
    DEFAULT_PLOT_RANGE_UK,
    DEFAULT_SINGLE_SLIDE_RANGE,
    DropdownTexts,
    Localization,
    Tab,
    TestIds,
} from 'utils';
import {
    projectionSlice,
    fetchHareData,
    updateProjectionTabSelection,
} from 'Redux/projectionsSlice';
import { WppSlider } from '@wppopen/components-library-react';
import { sidePanelSlice } from 'Redux/sidePanelSlice';
import { Main, MainWrapper, SelectionSection } from './CustomSelectionStyles';
import { useLegacyEffect } from 'hooks';

interface CustomSelectionProps {
    title: string;
    withSingleSlider?: boolean;
    localization: Localization;
}

export const CustomSelection: FC<CustomSelectionProps> = ({
    title,
    withSingleSlider,
    localization,
}) => {
    const dispatch = useDispatch();
    const {
        [localization]: {
            ['tabs']: {
                [title]: { selectionState, selectionData, isHareLoading, tortoiseUpdated },
            },
        },
    } = useSelector(projectionSlice);

    const {
        [localization]: { isTortoiseLoading },
    } = useSelector(sidePanelSlice);

    const { perAnnum, hareSelection, range, year, dropdownVariants } = selectionState;
    const { subPlot, aggregatedBy, xAxis } = hareSelection;
    const { isShowByShowing, isGroupByShowing, isXAxisShowing } = dropdownVariants;

    const DEFAULT_PLOT_RANGE_US =
        title === Tab.DRIVERS_OF_GROWTH ? DEFAULT_SINGLE_SLIDE_RANGE : DEFAULT_PLOT_RANGE;

    const SLIDER_RANGE =
        localization === Localization.US ? DEFAULT_PLOT_RANGE_US : DEFAULT_PLOT_RANGE_UK;

    const handleSelectionChange = (property: string, value: any) => {
        const isLocalChanges =
            property === 'perAnnum' || property === 'range' || property === 'year';
        if (isLocalChanges) {
            dispatch(
                updateProjectionTabSelection({
                    property,
                    value,
                    localization: localization,
                    tab: title,
                }),
            );
        } else {
            const groupByValue = property === 'aggregatedBy' ? value : aggregatedBy;
            const showByValue = property === 'subPlot' ? value : subPlot;
            const xAxisValue = property === 'xAxis' ? value : xAxis;
            dispatch(
                fetchHareData({
                    property,
                    value,
                    localization: localization,
                    tab: title,
                    previousState: {
                        ...hareSelection,
                    },
                    updatedState: {
                        aggregatedBy: groupByValue,
                        subPlot: showByValue,
                        xAxis: xAxisValue,
                    },
                }),
            );
        }
    };

    useLegacyEffect(() => {
        const handleRetrigger = () =>
            dispatch(
                fetchHareData({
                    property: false,
                    value: false,
                    localization: localization,
                    tab: title,
                    previousState: {
                        ...hareSelection,
                    },
                    updatedState: {
                        ...hareSelection,
                    },
                    retrigger: true,
                }),
            );
        !tortoiseUpdated && handleRetrigger();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tortoiseUpdated]);

    return (
        <Main>
            <MainWrapper>
                <SelectionSection isShowing={isGroupByShowing}>
                    <DropdownComponent
                        value={aggregatedBy}
                        handleChange={handleSelectionChange}
                        keyProp="aggregatedBy"
                        placeholder="Make selection"
                        label="Group by"
                        selectionData={selectionData}
                        isSingleSelection={true}
                        required={false}
                        dropdownWidth="165px"
                        containerWidth="165px"
                        icon="wpp-icon-info"
                        inputDisabled={isTortoiseLoading || isHareLoading}
                        iconDescription={DropdownTexts.GROUP_BY_DESCRIPTION}
                        wppSize="m"
                        dataTestId={TestIds.projection.plotlyContainer.groupBySection}
                    />
                </SelectionSection>
                <SelectionSection isShowing={isShowByShowing}>
                    <DropdownComponent
                        value={subPlot}
                        handleChange={handleSelectionChange}
                        keyProp="subPlot"
                        placeholder="Make selection"
                        label="Show by"
                        selectionData={selectionData}
                        isSingleSelection={true}
                        required={false}
                        dropdownWidth="165px"
                        containerWidth="165px"
                        icon="wpp-icon-info"
                        inputDisabled={isTortoiseLoading || isHareLoading}
                        iconDescription={DropdownTexts.SHOW_BY_DESCRIPTION}
                        wppSize="m"
                        dataTestId={TestIds.projection.plotlyContainer.showBySection}
                    />
                </SelectionSection>
                <SelectionSection isShowing={isXAxisShowing}>
                    <DropdownComponent
                        value={xAxis}
                        handleChange={handleSelectionChange}
                        keyProp="xAxis"
                        placeholder="Make selection"
                        label="X-axis"
                        selectionData={selectionData}
                        isSingleSelection={true}
                        required={false}
                        dropdownWidth="165px"
                        containerWidth="165px"
                        icon="wpp-icon-info"
                        inputDisabled={isTortoiseLoading || isHareLoading}
                        iconDescription={DropdownTexts.X_AXIS_DESCRIPTION}
                        wppSize="m"
                        dataTestId={TestIds.projection.plotlyContainer.xaxisSection}
                    />
                </SelectionSection>
                <SelectionSection>
                    <WppSlider
                        type={withSingleSlider ? 'single' : 'range'}
                        withValue={true}
                        value={withSingleSlider ? year : range}
                        step={1}
                        min={SLIDER_RANGE[0]}
                        max={SLIDER_RANGE[1]}
                        marks={true}
                        onWppChange={(e) =>
                            handleSelectionChange(
                                withSingleSlider ? 'year' : 'range',
                                e.detail.value,
                            )
                        }
                        continuous={true}
                        disabled={isTortoiseLoading || isHareLoading}
                        data-testid={TestIds.projection.plotlyContainer.yearsSection}
                    />
                </SelectionSection>
            </MainWrapper>
            <SelectionSection isShowing={Boolean(perAnnum)}>
                <SegmentControl
                    value={perAnnum}
                    handleChange={handleSelectionChange}
                    propertyKey="perAnnum"
                    wppSize="m"
                    dataTestId={TestIds.projection.plotlyContainer.segmentSection}
                />
            </SelectionSection>
        </Main>
    );
};
